import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import ScreenerTable from "../components/ScreenerTable";
import { Button } from "../components/ui/button";
import { Star, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Watchlist() {
    const [rows, setRows] = useState([]);
    const [watchlist, setWatchlist] = useState(new Set());
    const [sort, setSort] = useState({ field: "ticker", dir: "asc" });
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/watchlist");
            const items = res.data.items || [];
            setRows(sortRows(items, sort));
            setWatchlist(new Set(items.map((x) => x.ticker)));
        } finally {
            setLoading(false);
        }
    }, [sort]);

    useEffect(() => { reload(); }, [reload]);

    const handleSort = (field) => {
        setSort((p) => p.field === field
            ? { field, dir: p.dir === "asc" ? "desc" : "asc" }
            : { field, dir: field === "ticker" || field === "name" ? "asc" : "desc" });
    };

    const toggleWatch = async (ticker) => {
        try {
            await api.delete(`/watchlist/${ticker}`);
            toast.success(`Removed ${ticker} from watchlist`);
            reload();
        } catch { toast.error("Could not update watchlist"); }
    };

    return (
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
            <div className="mb-8 animate-fade-up">
                <div className="overline">Your list</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
                    Watchlist
                </h1>
                <p className="mt-2 text-muted-foreground">
                    {loading ? "Loading…" : `${rows.length} stock${rows.length === 1 ? "" : "s"} you're tracking.`}
                </p>
            </div>

            {!loading && rows.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-16 text-center" data-testid="watchlist-empty">
                    <div className="mx-auto h-12 w-12 rounded-2xl bg-background border border-border grid place-items-center">
                        <Star className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <h3 className="mt-4 font-heading text-xl font-semibold">Your watchlist is empty</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground max-w-sm mx-auto">
                        Star any ticker from the screener to start tracking compounders you care about.
                    </p>
                    <Button asChild className="mt-6 rounded-full">
                        <Link to="/dashboard" data-testid="goto-screener-btn">
                            Open screener <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Button>
                </div>
            ) : (
                <ScreenerTable
                    rows={rows}
                    sort={sort}
                    onSort={handleSort}
                    watchlist={watchlist}
                    onToggleWatch={toggleWatch}
                />
            )}
        </div>
    );
}

function sortRows(rows, { field, dir }) {
    const mult = dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
        const va = a[field]; const vb = b[field];
        if (typeof va === "string") return va.localeCompare(vb) * mult;
        return ((va ?? 0) - (vb ?? 0)) * mult;
    });
}
